import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type ReactNode,
  type UIEvent,
} from "react";
import { useSearchParams } from "react-router-dom";
import {
  ChevronDown,
  Globe,
  Languages,
  LayoutGrid,
  Link2,
  Map,
  MapPin,
  Monitor,
  Search,
  SlidersHorizontal,
  Tag,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useDebounce } from "@/hooks/useDebounce";
import {
  getUrlFilterMultiValues,
  getUrlFilterSlugValue,
  parseUrlActiveFilters,
  serializeUrlActiveFilters,
  type UrlActiveFilters,
  type UrlFilterMode,
} from "@/lib/url-active-filters";
import { cn } from "@/lib/utils";
import {
  EMPTY_GSC_FILTER_OPTIONS,
  GSC_FILTER_KEYS,
  type GscFilterKey,
  type GscFilterOptions,
} from "@/types/gsc";

const DEFAULT_FILTER_MODE: UrlFilterMode = "include";
const TEXT_DEBOUNCE_MS = 300;
const MULTI_SELECT_PAGE_SIZE = 50;

interface GscFiltersPanelProps {
  options?: GscFilterOptions;
  className?: string;
  isLoading?: boolean;
}

const sortOptions = (options: readonly string[]): string[] =>
  [...options].sort((a, b) => a.localeCompare(b));

const writeFiltersToSearchParams = (
  params: URLSearchParams,
  filters: UrlActiveFilters,
): void => {
  const serialized = serializeUrlActiveFilters(filters);
  if (serialized) params.set("filters", serialized);
  else params.delete("filters");
  params.delete("page");
};

interface GscMultiSelectFilterProps {
  label: string;
  icon: ReactNode;
  options: readonly string[];
  selectedValues: readonly string[];
  onChange: (values: string[]) => void;
  disabled?: boolean;
}

export const GscMultiSelectFilter = memo(function GscMultiSelectFilter({
  label,
  icon,
  options,
  selectedValues,
  onChange,
  disabled = false,
}: GscMultiSelectFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleCount, setVisibleCount] = useState(MULTI_SELECT_PAGE_SIZE);
  const debouncedQuery = useDebounce(searchQuery, TEXT_DEBOUNCE_MS);

  const sortedOptions = useMemo(
    (): string[] => sortOptions(options),
    [options],
  );

  // An empty selection and a full one both mean "no filter", and only the empty
  // form is stored: a full list would also exclude domains Jetsik has no page
  // for, which is not what ticking every box asks for.
  //
  // That leaves one stored state for two things the user wants to see
  // differently — "everything is on" and "I just cleared this to pick a few".
  // Which one is drawn is local to the popover; the query is the same either
  // way, so nothing about it belongs in the URL.
  const [showCleared, setShowCleared] = useState(false);

  const shownValues = useMemo((): readonly string[] => {
    if (selectedValues.length > 0) return selectedValues;
    return showCleared ? [] : sortedOptions;
  }, [selectedValues, showCleared, sortedOptions]);

  const isEverything = shownValues.length === sortedOptions.length;

  const emit = (values: string[], clearedByUser: boolean): void => {
    setShowCleared(values.length === 0 && clearedByUser);
    onChange(values.length === sortedOptions.length ? [] : values);
  };

  const selectedSet = useMemo(
    (): ReadonlySet<string> => new Set(shownValues),
    [shownValues],
  );

  const filteredOptions = useMemo((): string[] => {
    const query = debouncedQuery.trim().toLowerCase();
    if (!query) return sortedOptions;
    return sortedOptions.filter((option) =>
      option.toLowerCase().includes(query),
    );
  }, [debouncedQuery, sortedOptions]);

  const visibleOptions = useMemo(
    (): string[] => filteredOptions.slice(0, visibleCount),
    [filteredOptions, visibleCount],
  );

  useEffect(() => {
    setVisibleCount(MULTI_SELECT_PAGE_SIZE);
  }, [debouncedQuery, isOpen]);

  const handleOpenChange = (open: boolean): void => {
    setIsOpen(open);
    if (!open) setSearchQuery("");
  };

  const handleScroll = (event: UIEvent<HTMLDivElement>): void => {
    const target = event.currentTarget;
    const isBottom =
      target.scrollHeight - target.scrollTop <= target.clientHeight + 50;

    if (isBottom && visibleCount < filteredOptions.length) {
      setVisibleCount((prev) => prev + MULTI_SELECT_PAGE_SIZE);
    }
  };

  const handleToggle = (option: string): void => {
    if (selectedSet.has(option)) {
      emit(
        shownValues.filter((value) => value !== option),
        true,
      );
      return;
    }
    emit([...shownValues, option], false);
  };

  const hasSelection = selectedValues.length > 0;

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border border-border bg-background px-3 text-sm text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground cursor-pointer disabled:pointer-events-none disabled:opacity-50",
            hasSelection && "border-primary/40 text-foreground",
          )}
          aria-label={`Filter by ${label}`}
        >
          {icon}
          <span className="font-medium">{label}</span>
          {hasSelection ? (
            <span className="tabular-nums text-xs text-primary">
              {selectedValues.length}
            </span>
          ) : null}
          <ChevronDown className="size-3.5 opacity-50" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        className="w-[min(280px,calc(100vw-2rem))] rounded-xl border-border p-2"
        align="start"
      >
        {options.length > 6 ? (
          <div className="mb-2 flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2">
            <Search className="size-4 shrink-0 text-muted-foreground" />
            <input
              type="text"
              placeholder={`Search ${label.toLowerCase()}...`}
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              value={searchQuery}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                setSearchQuery(event.target.value)
              }
            />
          </div>
        ) : null}

        <button
          type="button"
          className="mb-1 flex w-full cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-muted"
          onClick={() => emit(isEverything ? [] : sortedOptions, true)}
        >
          <Checkbox
            checked={isEverything}
            onCheckedChange={() => {}}
            tabIndex={-1}
            className="pointer-events-none"
          />
          <span className="truncate text-sm font-medium text-foreground">
            Select all
          </span>
          <span className="ml-auto text-xs tabular-nums text-muted-foreground">
            {sortedOptions.length}
          </span>
        </button>

        <div
          className="hide-scrollbar max-h-[240px] space-y-0.5 overflow-y-auto px-1"
          onScroll={handleScroll}
        >
          {filteredOptions.length === 0 ? (
            <div className="py-5 text-center text-sm text-muted-foreground">
              No options.
            </div>
          ) : (
            visibleOptions.map((option) => {
              const isSelected = selectedSet.has(option);
              return (
                <button
                  key={option}
                  type="button"
                  className="flex w-full cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-muted"
                  onClick={() => handleToggle(option)}
                >
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => {}}
                    tabIndex={-1}
                    className="pointer-events-none"
                  />
                  <span
                    className={cn(
                      "truncate text-sm",
                      isSelected
                        ? "font-medium text-foreground"
                        : "text-foreground/85",
                    )}
                  >
                    {option}
                  </span>
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
});

interface GscSlugFilterProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const GscSlugFilter = memo(function GscSlugFilter({
  value,
  onChange,
  disabled = false,
}: GscSlugFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    if (!isOpen) setDraft(value);
  }, [isOpen, value]);

  const hasValue = value.trim().length > 0;

  const commit = (): void => {
    onChange(draft.trim());
    setIsOpen(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border border-border bg-background px-3 text-sm text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground cursor-pointer disabled:pointer-events-none disabled:opacity-50",
            hasValue && "border-primary/40 text-foreground",
          )}
          aria-label="Filter by slug"
        >
          <Link2 className="size-3.5 shrink-0 opacity-70" />
          <span className="font-medium">Slug</span>
          {hasValue ? (
            <span className="max-w-[6rem] truncate text-xs text-primary">
              {value}
            </span>
          ) : null}
          <ChevronDown className="size-3.5 opacity-50" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        className="w-[min(280px,calc(100vw-2rem))] space-y-2 rounded-xl border-border p-3"
        align="start"
      >
        <label className="text-xs font-medium text-muted-foreground">
          URL path contains
        </label>
        <div className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2">
          <Search className="size-4 shrink-0 text-muted-foreground" />
          <input
            type="text"
            placeholder="e.g. /blog/"
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            value={draft}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setDraft(event.target.value)
            }
            onKeyDown={(event) => {
              if (event.key === "Enter") commit();
            }}
          />
        </div>
        <div className="flex justify-end gap-2">
          {hasValue || draft.trim() ? (
            <button
              type="button"
              className="h-8 rounded-md px-2.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer"
              onClick={() => {
                setDraft("");
                onChange("");
                setIsOpen(false);
              }}
            >
              Clear
            </button>
          ) : null}
          <button
            type="button"
            className="h-8 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground hover:opacity-90 cursor-pointer"
            onClick={commit}
          >
            Apply
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
});

function GscFiltersPanelComponent({
  options = EMPTY_GSC_FILTER_OPTIONS,
  className,
  isLoading = false,
}: GscFiltersPanelProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const filtersParam = searchParams.get("filters");

  const activeFilters = useMemo(
    (): UrlActiveFilters => parseUrlActiveFilters(filtersParam),
    [filtersParam],
  );

  const urlQuery = getUrlFilterSlugValue(activeFilters[GSC_FILTER_KEYS.query]);
  const urlSlug = getUrlFilterSlugValue(activeFilters[GSC_FILTER_KEYS.slug]);

  // The draft is written from two directions: the user typing, and the URL
  // changing under it (a drill-down click on a Query cell). Only the first may
  // be written back, so `fromUser` travels with the value through the debounce
  // instead of being read separately — a separate flag would still hold the
  // pre-click value at the moment the effect runs.
  const [queryDraft, setQueryDraft] = useState({
    value: urlQuery,
    fromUser: false,
  });
  const debouncedQuery = useDebounce(queryDraft, TEXT_DEBOUNCE_MS);

  // Resetting the draft when the URL changes, done during render rather than in
  // an effect (https://react.dev/learn/you-might-not-need-an-effect).
  const [lastUrlQuery, setLastUrlQuery] = useState(urlQuery);
  if (lastUrlQuery !== urlQuery) {
    setLastUrlQuery(urlQuery);
    setQueryDraft({ value: urlQuery, fromUser: false });
  }

  const patchFilters = useCallback(
    (mutate: (filters: UrlActiveFilters) => void) => {
      setSearchParams((prev) => {
        const nextFilters = { ...parseUrlActiveFilters(prev.get("filters")) };
        mutate(nextFilters);
        writeFiltersToSearchParams(prev, nextFilters);
        return prev;
      });
    },
    [setSearchParams],
  );

  useEffect(() => {
    // Without this the debounce, still holding the pre-click value, reads as
    // "the box was cleared" and deletes the filter the click just applied.
    if (!debouncedQuery.fromUser) return;

    const trimmed = debouncedQuery.value.trim();
    if (trimmed === urlQuery) return;

    patchFilters((filters) => {
      if (!trimmed) {
        delete filters[GSC_FILTER_KEYS.query];
        return;
      }
      filters[GSC_FILTER_KEYS.query] = {
        mode: DEFAULT_FILTER_MODE,
        value: trimmed,
      };
    });
  }, [debouncedQuery, patchFilters, urlQuery]);

  const handleMultiChange = useCallback(
    (key: GscFilterKey, values: string[]) => {
      patchFilters((filters) => {
        if (values.length === 0) {
          delete filters[key];
          return;
        }
        filters[key] = { mode: DEFAULT_FILTER_MODE, values };
      });
    },
    [patchFilters],
  );

  const handleSlugChange = useCallback(
    (value: string) => {
      patchFilters((filters) => {
        if (!value) {
          delete filters[GSC_FILTER_KEYS.slug];
          return;
        }
        filters[GSC_FILTER_KEYS.slug] = {
          mode: DEFAULT_FILTER_MODE,
          value,
        };
      });
    },
    [patchFilters],
  );

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card px-3 py-2.5",
        isLoading && "opacity-70",
        className,
      )}
      role="search"
      aria-label="Detail table filters"
      aria-busy={isLoading}
    >
      <div className="flex items-center gap-1.5 pr-1 text-[11px] font-semibold tracking-[0.08em] text-muted-foreground uppercase">
        <SlidersHorizontal className="size-3.5" aria-hidden />
        Filters
      </div>

      <div className="relative min-w-[12rem] flex-1 basis-[14rem] sm:max-w-[18rem]">
        <Search className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground" />
        <input
          type="search"
          value={queryDraft.value}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            setQueryDraft({ value: event.target.value, fromUser: true })
          }
          placeholder="Filter query..."
          disabled={isLoading}
          className="h-9 w-full rounded-full border border-border bg-background pr-3 pl-8 text-sm outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/40 focus-visible:ring-[3px] disabled:opacity-50"
          aria-label="Filter by query"
        />
      </div>

      <GscMultiSelectFilter
        label="Domain"
        icon={<Globe className="size-3.5 shrink-0 opacity-70" />}
        options={options.domain}
        selectedValues={getUrlFilterMultiValues(
          activeFilters[GSC_FILTER_KEYS.domain],
        )}
        onChange={(values) => handleMultiChange(GSC_FILTER_KEYS.domain, values)}
        disabled={isLoading}
      />

      <GscMultiSelectFilter
        label="Device"
        icon={<Monitor className="size-3.5 shrink-0 opacity-70" />}
        options={options.device}
        selectedValues={getUrlFilterMultiValues(
          activeFilters[GSC_FILTER_KEYS.device],
        )}
        onChange={(values) => handleMultiChange(GSC_FILTER_KEYS.device, values)}
        disabled={isLoading}
      />

      <GscMultiSelectFilter
        label="Country"
        icon={<MapPin className="size-3.5 shrink-0 opacity-70" />}
        options={options.country}
        selectedValues={getUrlFilterMultiValues(
          activeFilters[GSC_FILTER_KEYS.country],
        )}
        onChange={(values) =>
          handleMultiChange(GSC_FILTER_KEYS.country, values)
        }
        disabled={isLoading}
      />

      <GscSlugFilter
        value={urlSlug}
        onChange={handleSlugChange}
        disabled={isLoading}
      />

      <GscMultiSelectFilter
        label="GEO"
        icon={<Map className="size-3.5 shrink-0 opacity-70" />}
        options={options.geo}
        selectedValues={getUrlFilterMultiValues(
          activeFilters[GSC_FILTER_KEYS.geo],
        )}
        onChange={(values) => handleMultiChange(GSC_FILTER_KEYS.geo, values)}
        disabled={isLoading}
      />

      <GscMultiSelectFilter
        label="Site Type"
        icon={<LayoutGrid className="size-3.5 shrink-0 opacity-70" />}
        options={options.site_type}
        selectedValues={getUrlFilterMultiValues(
          activeFilters[GSC_FILTER_KEYS.site_type],
        )}
        onChange={(values) =>
          handleMultiChange(GSC_FILTER_KEYS.site_type, values)
        }
        disabled={isLoading}
      />

      <GscMultiSelectFilter
        label="Brand"
        icon={<Tag className="size-3.5 shrink-0 opacity-70" />}
        options={options.brand_keyword}
        selectedValues={getUrlFilterMultiValues(
          activeFilters[GSC_FILTER_KEYS.brand_keyword],
        )}
        onChange={(values) =>
          handleMultiChange(GSC_FILTER_KEYS.brand_keyword, values)
        }
        disabled={isLoading}
      />

      <GscMultiSelectFilter
        label="Language"
        icon={<Languages className="size-3.5 shrink-0 opacity-70" />}
        options={options.language}
        selectedValues={getUrlFilterMultiValues(
          activeFilters[GSC_FILTER_KEYS.language],
        )}
        onChange={(values) =>
          handleMultiChange(GSC_FILTER_KEYS.language, values)
        }
        disabled={isLoading}
      />
    </div>
  );
}

export const GscFiltersPanel = memo(GscFiltersPanelComponent);
GscFiltersPanel.displayName = "GscFiltersPanel";
